// Endpoints that must never trigger the silent-refresh retry loop.
const AUTH_PATHS = ['auth/authenticate', 'auth/refresh-token', 'auth/csrf', 'auth/logout'];

const baseUrl = `${process.env.REACT_APP_API_BASE_URL || ''}/api/v1/`;

// The XSRF-TOKEN cookie is set on the API's host, so document.cookie can only read it
// when the SPA and API share a host. Cross-origin deployments (herokuapp topology) must
// take the token from the /auth/csrf response body instead. We echo it as the
// X-XSRF-TOKEN header; the browser sends the XSRF-TOKEN cookie itself, which is what
// the server compares the header against (double submit).
let csrfToken = null;

const getCsrfToken = () => {
  if (csrfToken) {
    return csrfToken;
  }
  const match = document.cookie.match(/(?:^|;\s*)XSRF-TOKEN=([^;]+)/);
  return match ? decodeURIComponent(match[1]) : null;
};

/**
 * Called when the server-side CSRF state is invalidated (logout deletes the
 * XSRF-TOKEN cookie; a concurrent tab's /auth/csrf re-issuance rotates it).
 * The next mutating request re-bootstraps.
 */
export const resetCsrfToken = () => {
  csrfToken = null;
  csrfBootstrap = null;
};

// Bootstrap CSRF: fetch a token once when we hold none.
let csrfBootstrap = null;
const ensureCsrf = () => {
  if (getCsrfToken()) {
    return Promise.resolve();
  }
  if (!csrfBootstrap) {
    csrfBootstrap = fetch(`${baseUrl}auth/csrf`, {credentials: 'include'})
      .then(res => (res.ok ? res.json() : null))
      .then(data => {
        if (data && data.token) {
          csrfToken = data.token;
        }
      })
      .catch(() => {})
      .finally(() => {
        csrfBootstrap = null;
      });
  }
  return csrfBootstrap;
};

// Single-flight silent refresh: parallel 401s share one POST /auth/refresh-token.
let refreshPromise = null;
const refreshSession = () => {
  if (!refreshPromise) {
    refreshPromise = fetch(`${baseUrl}auth/refresh-token`, {method: 'POST', credentials: 'include'})
      .catch(() => ({ok: false}))
      .finally(() => {
        // Let the Set-Cookie from the response settle before the next read.
        refreshPromise = null;
      });
  }
  return refreshPromise;
};

const isAuthPath = url => AUTH_PATHS.some(path => url.startsWith(path));

class ApiRequest {

  static async apiRequest(
    {
      method,
      url,
      body,
      params,
      isFormData,
      responseType,
      token,
      noResponse,
      version,
      getVersion,
      addVersion,
      fileName,
      attempt = 0
    }
  ) {
    const paramsString = params && Object.keys(params).length
      ? `?${Object.keys(params).map(param => param + '=' + params[param]).join('&')}`
      : '';

    const isMutating = method !== 'GET' && method !== 'HEAD';
    if (isMutating) {
      await ensureCsrf();
    }

    let headers = !isFormData ? {'Content-Type': 'application/json'} : {};
    const csrfToken = getCsrfToken();
    if (isMutating && csrfToken) {
      headers = {...headers, 'X-XSRF-TOKEN': csrfToken};
    }
    if (token) {
      // Explicit header token (kept for tools/scripts; the app itself uses cookies).
      headers = {...headers, 'Authorization': token};
    }
    if (version) {
      headers = {...headers,
        'If-Match': version
      };
    }

    let result;

    const res = await fetch(`${baseUrl}${url}${paramsString}`, {
      method,
      credentials: 'include',
      headers,
      body: !isFormData
        ? JSON.stringify(body)
        : body
    });

    // Session may have expired mid-use: refresh once (single-flight) and retry.
    if (res.status === 401 && !isAuthPath(url) && attempt === 0) {
      const refreshed = await refreshSession();
      if (refreshed && refreshed.ok) {
        return ApiRequest.apiRequest({method, url, body, params, isFormData, responseType, token, noResponse, version, getVersion, addVersion, fileName, attempt: attempt + 1});
      }
      resetCsrfToken();
      window.dispatchEvent(new Event('wolte:session-expired'));
    }

    // CSRF can fail independently of the session: the token we hold is stale when the
    // server-side XSRF-TOKEN cookie was replaced (logout deleted it; a concurrent tab's
    // /auth/csrf re-issued it). For auth endpoints a CSRF failure surfaces as 401 (the
    // entry point fires for anonymous users), for everything else as 403. Re-seed the
    // token and retry once before giving up.
    const isCsrfFailure = attempt === 0 &&
      ((res.status === 401 && (url.startsWith('auth/logout') || url.startsWith('auth/authenticate') || url.startsWith('auth/refresh-token'))) ||
       (res.status === 403 && isMutating));
    if (isCsrfFailure) {
      resetCsrfToken();
      await ensureCsrf();
      if (getCsrfToken()) {
        return ApiRequest.apiRequest({method, url, body, params, isFormData, responseType, token, noResponse, version, getVersion, addVersion, fileName, attempt: attempt + 1});
      }
    }

    if (res.status === 200 || res.status === 201 || res.status === 204) {
      if (getVersion) {
        result = res.headers.get("Etag").toString();
      } else  if (method === 'DELETE' || noResponse === true) {
        result = res.status;
      } else if (responseType === 'text') {
        result = await res.text();
      } else if (responseType === 'blob') {
        result = await res.blob();
        const url = window.URL.createObjectURL(result);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = fileName || 'file';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
      } else {
        result = await res.json();
      }
      if (addVersion) {
        result.version = res.headers.get("Etag").toString();
      }
    } else {
      const responseError = {
        type: 'Error',
        message: res.message || 'Something went wrong',
        data: res.data || '',
        status: res.status || '',
      };
      console.log(responseError);
      result = responseError;
    }

    return result;
  }
}

export default ApiRequest;
